const { useMemo } = require('react');

const { connect } = require('react-redux');
const { bindActionCreators } = require('redux');
const { FormProvider } = require('checkout-off-form-validation');
const { useI18n } = require('nordic/i18n');
const { Snackbar } = require('@andes/snackbar');
const { Title } = require('@andes/typography');

const stepActions = require('../../../spa/actions/step');
const { getQueryParams } = require('../../../utils/Dom');
const { STEP_NEXT } = require('../../../spa/actions/types');
const translate = require('../../../translation');
const { DEVICE_TYPE, DELAY } = require('../../../../constants/commons');
const { AddressForm } = require('./AddressForm');
const { FormAddressContext } = require('./useFormAddress');
const { useField, FieldContext } = require('../Components/Form/Field');

const SNACKBAR_DELAY = DELAY.THREE_SECONDS;

const transformReusableAddress = ({
  zip_code,
  street_name,
  street_number,
  floor,
  city_name,
  state_name,
} = {}) => ({
  zipcode: zip_code,
  streetName: street_name,
  streetNumber: street_number,
  floor,
  cityName: city_name,
  stateName: state_name,
});

const transformPayer = ({
  name,
  email,
  phone: { area_code, number } = {},
} = {}) => ({
  name,
  email,
  phoneNumber: [area_code, number].join(''),
});

const getAddressInfo = (
  i18n,
  hasMoreThanOneCity,
  { zipcode, selectedState, selectedCity } = {},
) => {
  if (zipcode) {
    return hasMoreThanOneCity
      ? i18n.gettext('CP {0} - {1}.', zipcode, selectedState)
      : i18n.gettext(
          'CP {0} - {1}, {2}.',
          zipcode,
          selectedState,
          selectedCity,
        );
  }

  return '';
};

const getValueIfIsTrue = (isTrue) => (value) => (isTrue ? value : '');

const AddressFormV2 = (props) => {
  const { flow, history, stepAction, deviceType, countryId, data, configs } =
    props;

  const { i18n } = useI18n();
  const translations = translate(i18n);

  const hasError = data?.error_message !== '';
  const { rebrandingMP, platform } = configs;

  const formConfigs = {
    neighborhood: {
      required: data?.is_neighborhood_required,
      show: data?.show_neighborhood_input,
    },
    email: {
      show: data?.show_email_input,
    },
    betweenStreet: {
      show: data?.show_between_street_input,
    },
    floor: {
      show: data?.show_floor_input,
    },
  };

  const receiverAddress = transformReusableAddress(data?.receiver_address);
  const payer = transformPayer(data?.payer);
  const getIfIsReusable = getValueIfIsTrue(
    data?.address_step === 'reused_address',
  );

  const inputs = {
    neighborhood: data?.neighborhood,
    street: data?.street || getIfIsReusable(receiverAddress.streetName),
    streetNumber:
      data?.street_number || getIfIsReusable(receiverAddress.streetNumber),
    floor: data?.floor || getIfIsReusable(receiverAddress.floor),
    residenceOption: data?.residence_option || 'residencial',
    description: data?.description,
    name: data?.name || getIfIsReusable(payer.name),
    phoneNumber: data?.phone_number || getIfIsReusable(payer.phoneNumber),
    email: data?.email || getIfIsReusable(payer.email),
    zipcode: data?.zip_code || getIfIsReusable(receiverAddress.zipcode),
    selectedState: data?.state || getIfIsReusable(receiverAddress.stateName),
    selectedCity:
      data?.selected_city || getIfIsReusable(receiverAddress.cityName),
  };

  const hasMoreThanOneCity = data?.cities?.length > 1;
  const isMobile = deviceType === DEVICE_TYPE.MOBILE;

  const { context } = useField({
    values: {
      ...inputs,
      selectedAddress: getAddressInfo(i18n, hasMoreThanOneCity, inputs),
      hasMoreThanOneCity,
      cities: data?.cities,

      addressStep: data?.address_step,
      showAddressModal: data?.address_step === 'modal',
      isAddressReusable: [
        'modal',
        'modify_address',
        'choice_address',
        'reused_address',
      ].includes(data?.address_step),

      siteId: platform?.siteId,
    },
  });

  const valueCached = useMemo(() => ({
    isMobile,
    deviceType,
    countryId,
    formConfigs,
    isInRebrand: rebrandingMP,
    receiverAddress,
    payer,
  }));

  const handleShippingAction = (inputs) => {
    const urlParams = getQueryParams();
    const defaultData = {
      urlParams,
      type: flow.type,
    };
    const payload = {
      ...inputs,
      isNewInterface: true,
    };

    stepAction[STEP_NEXT](
      payload,
      flow.id,
      defaultData,
      flow.type,
      urlParams,
      history,
    );
  };

  return (
    <FieldContext.Provider value={context}>
      <FormAddressContext.Provider value={valueCached}>
        <div className="shipping-generics shipping-address-form-v2">
          <Title size="xs" className="mt-40i mb-16i" component="div">
            {translations.SHIPPING_TITLE_ADDRESS_FORM}
          </Title>

          <FormProvider>
            <AddressForm handleShippingAction={handleShippingAction} />
          </FormProvider>

          <Snackbar
            delay={SNACKBAR_DELAY}
            message={data.error_message}
            show={hasError}
          />
        </div>
      </FormAddressContext.Provider>
    </FieldContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  flow: state.page.flow,
  configs: state.configurations,
  countryId: state.configurations.platform.countryId,
});
const mapDispatchToProps = (dispatch) => ({
  stepAction: bindActionCreators(stepActions, dispatch),
});

module.exports = {
  AddressFormV2: connect(mapStateToProps, mapDispatchToProps)(AddressFormV2),
};
